import type { CorrelationsFilters, FilterKey } from '@library/types/filters';
import { useMemo, useState } from 'react';
import { Correlations } from '@bo-components/correlations';
import { DEFAULT_CORRELATIONS_FILTERS } from '@bo-constants/filters';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { LeagueDependentFilters } from '@library/components/LeagueDependentFilters';
import { PageTitle } from '@library/components/PageTitle';
import { UploadCorrelationsCSV } from '@bo-components/correlations/UploadCorrelationsCSV';
import { getParameterValue } from '@library/component-utils/filters';
import { removeDuplicatesInArray } from '@bo-utils/common';
import { useCorrelationsDashboard } from '@bo-hooks/correlations/useCorrelationsDashboard';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';

export const CorrelationsDashboard = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Correlations' });

  const {
    changeFilterValue,
    durations,
    filters,
    handleMultiSelectDurations,
    handleSelectLeague,
    leagueInfo,
    leaguesList,
    selectedDurations,
    selectedLeaguesDurationAttributes,
    setFilters
  } = useDashBoard({
    dashboardName: 'CORRELATIONS'
  });

  const [searchValue, setSearchValue] = useState('');

  const {
    isSameTeam: selectedIsSameTeam,
    firstChoice: selectedFirstChoice,
    position1: selectedFirstPosition,
    position2: selectedSecondPosition,
    secondChoice: selectedSecondChoice,
    statType1: selectedStatType1,
    statType2: selectedStatType2
  } = filters as CorrelationsFilters;

  const correlationsQueryParams = useMemo(() => {
    return {
      statType1: getParameterValue(selectedStatType1),
      statType2: getParameterValue(selectedStatType2),
      firstPosition: getParameterValue(selectedFirstPosition),
      secondPosition: getParameterValue(selectedSecondPosition),
      isSameTeam: getParameterValue(selectedIsSameTeam),
      firstChoice: getParameterValue(selectedFirstChoice),
      secondChoice: getParameterValue(selectedSecondChoice)
    };
  }, [
    selectedStatType1,
    selectedStatType2,
    selectedFirstPosition,
    selectedSecondPosition,
    selectedFirstChoice,
    selectedSecondChoice,
    selectedIsSameTeam
  ]);

  const { id: baseLeagueId } = leagueInfo ?? {};

  const statTypes = selectedLeaguesDurationAttributes
    .map((leagueDurationAttributes) => leagueDurationAttributes.statTypes)
    .flat();

  const positionTypes = selectedLeaguesDurationAttributes
    .map((leagueDurationAttributes) => leagueDurationAttributes.positions)
    .flat();

  const allStats = removeDuplicatesInArray(statTypes);

  const handleChangeLeague = (newLeagueId: string) => {
    handleSelectLeague(newLeagueId);
    setFilters(DEFAULT_CORRELATIONS_FILTERS);
  };

  const handleChangeLeagueChange = (newLeagueId: string) => {
    handleChangeLeague(newLeagueId);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleMultiSelectDurationsChange = (value: Array<string>) => {
    handleMultiSelectDurations(value);
  };

  const handleFilterChange = (
    filterName: FilterKey,
    value: string | Array<string>
  ) => {
    changeFilterValue(filterName, value);
  };

  const handleReset = () => {
    setFilters(DEFAULT_CORRELATIONS_FILTERS);
    handleSearch('');
  };

  const { allStatTypes } = useCorrelationsDashboard();

  return (
    <div className="h-full w-auto">
      <section className="border-soFresh-140 w-auto border-b px-6 pb-2 pt-3">
        <PageTitle title="Correlations" />
        <div className="grid grid-cols-[repeat(13,1fr)] gap-4 pt-4">
          <div className="col-span-12">
            <div className="flex">
              <GlobalFilters
                correlationsFilters
                disabled={false}
                durations={durations}
                handleMultiSelectDurations={handleMultiSelectDurationsChange}
                handleSearch={handleSearch}
                handleSelectLeague={handleChangeLeagueChange}
                leaguesList={leaguesList}
                searchDisabled={false}
                searchValue={searchValue}
                searchPlaceholder="Search Correlations"
                selectedDurations={selectedDurations}
                selectedLeagueId={baseLeagueId}
              />
              <UploadCorrelationsCSV />
            </div>
          </div>
          <div className="col-span-12 flex items-center">
            <LeagueDependentFilters
              correlationsFilter
              disabled={false}
              filters={filters}
              handleCorrelationsChange={handleFilterChange}
              onReset={handleReset}
              statTypeValues={allStats}
              positionValues={positionTypes}
            />
          </div>
        </div>
      </section>
      <Correlations
        allStatTypes={allStatTypes}
        queryParams={correlationsQueryParams}
      />
    </div>
  );
};
